import * as dxo from '@fullstory/proto/fs/services/dx-objects';
import {
  createRequestCancellationAction,
  RequestDispatch,
  createGRPCAction,
  RequestActionType,
} from '@fullstory/redux-util';
import { defaultCacheKey } from '@fullstory/requests';
import {
  ComputeRetentionMetricResponseWithId,
  RetentionMetricsRequestBody,
  RetentionMetricsRequestTypes,
} from './types';

import { Flags } from '@fullstory/client-api';
import {
  ComputeRetentionMetricsResponse,
  QueryCacheOptions,
  RetentionMetricsServicePublic,
} from '@fullstory/proto/fs/services/alexa';
import { CanAccessRequest } from '@fullstory/proto/fs/shared/accesscontrol';
import { timestamp, Timestamp } from '@fullstory/proto/google/protobuf';
import { createAppCacheActions } from '@fullstory/redux-util/utils/appCache';
import { createAppCacheRequestAction } from '@fullstory/redux-util/utils/appCache/actions';
import { ActionType } from '@fullstory/redux-util/utils/types';
import { equalArrays, isDataFresh } from '@fullstory/searchiecache';
import { hashObject } from '@fullstory/util/hash';
import { UID } from '@fullstory/util/uid';
import { BatchOptions } from '@fullstory/util/xhr';
import { AppDispatch } from '../../types';
import { metricsCacheOptionsRefresh } from '../metrics';
import { SearchCacheKey } from '../searchMeta';
import { retentionMetricsRequestBodyToComputeRetentionMetricRequest } from './utils';

const retentionMetricCacheKeysToInvalidate = (orgId: string) => {
  const endpoint = dxo.RetentionMetricsPublic.GetAllRetentionMetricsEndpoint;
  return [
    defaultCacheKey(endpoint, {
      includeArchived: true,
      orgId,
    }),
  ];
};

export const RetentionMetricsRequestActions = {
  getAllRetentionMetrics: (orgId: string, force?: boolean) =>
    createGRPCAction<typeof dxo.RetentionMetricsPublic.GetAllRetentionMetrics>()(
      {
        type: RetentionMetricsRequestTypes.GetAllRetentionMetricsRequest,
        grpcFunction: dxo.RetentionMetricsPublic.GetAllRetentionMetrics,
        body: { orgId, includeArchived: true },
        force,
        readWrite: 'read',
      },
      RetentionMetricsRequestTypes.GetAllRetentionMetricsSuccess,
      RetentionMetricsRequestTypes.GetAllRetentionMetricsFailure,
    ),
  setRetentionMetric: (body: dxo.SetRetentionMetricRequest) =>
    createGRPCAction<typeof dxo.RetentionMetricsPublic.SetRetentionMetric>()(
      {
        type: RetentionMetricsRequestTypes.SetRetentionMetricRequest,
        grpcFunction: dxo.RetentionMetricsPublic.SetRetentionMetric,
        body,
        force: true,
        readWrite: 'write',
        invalidateCacheKey: retentionMetricCacheKeysToInvalidate(body.orgId),
      },
      RetentionMetricsRequestTypes.SetRetentionMetricSuccess,
      RetentionMetricsRequestTypes.SetRetentionMetricFailure,
    ),
  markRetentionMetricViewed: (body: dxo.MarkRetentionMetricViewedRequest) =>
    createGRPCAction<
      typeof dxo.RetentionMetricsPublic.MarkRetentionMetricViewed,
      { lastViewed: timestamp }
    >({
      // NB: this is a hack - lastViewed timestamp is set by the server, but the server does not return the timestamp
      // Settings to Timestamp.now() is close enough
      modify: () => ({ lastViewed: Timestamp.now() }),
    })(
      {
        type: RetentionMetricsRequestTypes.MarkViewedRequest,
        grpcFunction: dxo.RetentionMetricsPublic.MarkRetentionMetricViewed,
        body,
        force: true,
        readWrite: 'write',
      },
      RetentionMetricsRequestTypes.MarkViewedSuccess,
      RetentionMetricsRequestTypes.MarkViewedFailure,
    ),
  starRetentionMetric: (body: dxo.StarRetentionMetricRequest) =>
    createGRPCAction<typeof dxo.RetentionMetricsPublic.StarRetentionMetric>()(
      {
        type: RetentionMetricsRequestTypes.StarRetentionMetricRequest,
        grpcFunction: dxo.RetentionMetricsPublic.StarRetentionMetric,
        body,
        force: true,
        invalidateCacheKey: retentionMetricCacheKeysToInvalidate(body.orgId),
        readWrite: 'write',
      },
      RetentionMetricsRequestTypes.StarRetentionMetricSuccess,
      RetentionMetricsRequestTypes.StarRetentionMetricFailure,
    ),
  archiveRetentionMetric: (body: dxo.ArchiveRetentionMetricRequest) =>
    createGRPCAction<typeof dxo.RetentionMetricsPublic.ArchiveRetentionMetric>()(
      {
        type: RetentionMetricsRequestTypes.ArchiveRetentionMetricRequest,
        grpcFunction: dxo.RetentionMetricsPublic.ArchiveRetentionMetric,
        body,
        force: true,
        invalidateCacheKey: retentionMetricCacheKeysToInvalidate(body.orgId),
        readWrite: 'write',
      },
      RetentionMetricsRequestTypes.ArchiveRetentionMetricSuccess,
      RetentionMetricsRequestTypes.ArchiveRetentionMetricFailure,
    ),
  createUnnamedRetentionMetric: (body: dxo.CreateUnnamedRetentionMetricRequest) =>
    createGRPCAction<typeof dxo.RetentionMetricsPublic.CreateUnnamedRetentionMetric>()(
      {
        type: RetentionMetricsRequestTypes.CreateUnnamedRetentionMetricRequest,
        grpcFunction: dxo.RetentionMetricsPublic.CreateUnnamedRetentionMetric,
        body,
        readWrite: 'write',
      },
      RetentionMetricsRequestTypes.CreateUnnamedRetentionMetricSuccess,
      RetentionMetricsRequestTypes.CreateUnnamedRetentionMetricFailure,
    ),
  getUnnamedRetentionMetric: (body: dxo.GetUnnamedRetentionMetricRequest) =>
    createGRPCAction<typeof dxo.RetentionMetricsPublic.GetUnnamedRetentionMetric>()(
      {
        type: RetentionMetricsRequestTypes.GetUnnamedRetentionMetricRequest,
        grpcFunction: dxo.RetentionMetricsPublic.GetUnnamedRetentionMetric,
        body,
        readWrite: 'read',
      },
      RetentionMetricsRequestTypes.GetUnnamedRetentionMetricSuccess,
      RetentionMetricsRequestTypes.GetUnnamedRetentionMetricFailure,
    ),
  computeRetentionMetric: (
    body: RetentionMetricsRequestBody,
    searchCacheKey: SearchCacheKey,
    flags: Flags,
    batchOptions?: BatchOptions,
    force = false,
    uid?: UID,
  ) => {
    const reqBody = retentionMetricsRequestBodyToComputeRetentionMetricRequest(body);
    return createGRPCAction<
      typeof RetentionMetricsServicePublic.ComputeRetentionMetrics,
      ComputeRetentionMetricResponseWithId
    >({
      // this gives retention metrics data unique and stable IDs that are used as keys,
      // necessary for providing LabelGroup keys
      modify: (rsp: ComputeRetentionMetricsResponse): ComputeRetentionMetricResponseWithId =>
        rsp.retention && rsp.retention.retentions
          ? {
              ...rsp,
              retention: {
                ...rsp.retention,
                retentions: rsp.retention.retentions.map(r => ({ ...r, id: UID() })),
              },
            }
          : { ...rsp, retention: null },
      cacheKey: url => {
        const { requestAnalytics, cacheOptions, ...requestCacheData } = reqBody;
        return `${url}:${hashObject(requestCacheData)}:${hashObject(searchCacheKey)}`;
      },
      uid,
    })(
      {
        type: RetentionMetricsRequestTypes.ComputeRetentionMetricRequest,
        grpcFunction: RetentionMetricsServicePublic.ComputeRetentionMetrics,
        body: reqBody,
        settings: {
          headers: {
            'GRPC-Timeout': '1M', // allow for 1 minute before context deadline exceeded
            'X-css-as-elements': 'true',
          },
          batchOptions,
        },
        readWrite: 'write',
        force,
      },
      RetentionMetricsRequestTypes.ComputeRetentionMetricSuccess,
      RetentionMetricsRequestTypes.ComputeRetentionMetricFailure,
    );
  },
  getAccess: (body: CanAccessRequest) =>
    createGRPCAction<typeof dxo.RetentionMetricsPublic.GetRetentionMetricsAccess>()(
      {
        type: RetentionMetricsRequestTypes.RetentionMetricsAccessRequest,
        grpcFunction: dxo.RetentionMetricsPublic.GetRetentionMetricsAccess,
        body,
        readWrite: 'read',
      },
      RetentionMetricsRequestTypes.RetentionMetricsAccessSuccess,
      RetentionMetricsRequestTypes.RetentionMetricsAccessFailure,
    ),
};

export const RetentionMetricsActions = createAppCacheActions(
  RetentionMetricsRequestTypes.RetentionMetricsAppCacheIsLoading,
  RetentionMetricsRequestTypes.RetentionMetricsAppCacheLoadFailed,
  RetentionMetricsRequestTypes.RetentionMetricsAppCacheRefetching,
  RetentionMetricsRequestTypes.RetentionMetricsAppCacheHasData,
  RetentionMetricsRequestTypes.RetentionMetricsAppCacheFresh,
  RetentionMetricsRequestTypes.RetentionMetricsAppCacheSwap,
  RetentionMetricsRequestTypes.RetentionMetricsAppCacheClear,
)<ComputeRetentionMetricResponseWithId>();
export type RetentionMetricsActions =
  | ActionType<typeof RetentionMetricsActions>
  | RequestActionType<typeof RetentionMetricsRequestActions>;

export interface RetentionMetricsPayload {
  body: RetentionMetricsRequestBody;
  flags: Flags;
  searchCacheKey: SearchCacheKey;
  cacheOptions: QueryCacheOptions;
}

export const RetentionMetricsAsyncActions = {
  appCache: (batchOptions?: BatchOptions, uid?: UID, forceSwap?: boolean) => ({
    action: createAppCacheRequestAction<
      ComputeRetentionMetricResponseWithId,
      RetentionMetricsPayload
    >()({
      query: async (
        dispatch: RequestDispatch<RetentionMetricsActions>,
        payload: RetentionMetricsPayload,
        isRefresh: boolean,
      ) => {
        const cacheOptions = isRefresh ? metricsCacheOptionsRefresh : payload.cacheOptions;
        const data = await dispatch(
          RetentionMetricsRequestActions.computeRetentionMetric(
            {
              ...payload.body,
              cacheOptions,
            },
            payload.searchCacheKey,
            payload.flags,
            batchOptions,
            isRefresh, // if user specifically requested a refresh, force the request action
            isRefresh ? undefined : uid, // if it's a refresh, don't send the UID
          ),
        );
        return data;
      },
      loading: RetentionMetricsActions.appCacheIsLoading,
      failed: RetentionMetricsActions.appCacheLoadFailed,
      refetching: RetentionMetricsActions.appCacheIsRefetching,
      hasData: RetentionMetricsActions.appCacheHasData,
      fresh: RetentionMetricsActions.appCacheFresh,
      isDataFresh: (payload: RetentionMetricsPayload, data: ComputeRetentionMetricResponseWithId) =>
        isDataFresh(payload.cacheOptions, Timestamp.mustDate(data.computedAt), new Date()),
      isSimiliar: (
        newData: ComputeRetentionMetricResponseWithId,
        oldData: ComputeRetentionMetricResponseWithId,
      ) => {
        if (!oldData.retention || !newData.retention) return false;
        return equalArrays(oldData.retention.retentions, newData.retention.retentions);
      },
      forceSwap,
    }),
    cancelSingle: (dispatch: AppDispatch, uid: UID) => {
      dispatch(createRequestCancellationAction(uid));
    },
  }),
};
